import { queryOptions, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { intentionApi } from '../api/intentionApi'
import type { INTENTION_TYPE } from '../models/Intention.enums'
import type { TTypeIntention } from '../models/Intention.types'
import { getIntentionQueryOptions } from './useIntentionQuery'

export const getIntentionsByTypeQueryOptions = <Type extends INTENTION_TYPE.ARRIVAL | INTENTION_TYPE.BIRTHDAY>(
  enabled: boolean,
  type: Type,
  queryClient: QueryClient | null,
) =>
  queryOptions({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['intentions', type],
    queryFn: async ({ signal }) => {
      const specialIntentions = await intentionApi.getIntentionsByType(type, { signal })
      if (queryClient) {
        specialIntentions.forEach((intention) => {
          queryClient.setQueryData(getIntentionQueryOptions(intention.id, undefined, type).queryKey, intention)
        })
      }
      // Normalize the data so that there is only one source of truth #normalizeIntentions
      return specialIntentions.map(({ id }) => id)
    },
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000 * 60, // 1 hour
    gcTime: 60 * 1000 * 60, // 1 hour
    enabled,
  })

export const useIntentionsByTypeQuery = <Type extends INTENTION_TYPE.ARRIVAL | INTENTION_TYPE.BIRTHDAY>(
  enabled: boolean,
  type: Type,
) => {
  const queryClient = useQueryClient()
  const query = useQuery(getIntentionsByTypeQueryOptions(enabled, type, queryClient))
  const intentions = useQueries({
    queries: query.data ? query.data.map((id) => getIntentionQueryOptions(id, undefined, type)) : [],
    combine: (results) =>
      results
        .map((result) => result.data)
        .filter((intention: TTypeIntention<Type> | undefined): intention is TTypeIntention<Type> => !!intention),
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    intentions,
  }
}
